import jwtAuthStore from "shared/store/jwtAuthStore";
import JwtApiService from "./JwtApiService";

/**
 * Service for handling JWT token-based authentication
 */
export const JwtAuthService = {
  /**
   * Verify a JWT token with the server
   * @param token JWT token to verify
   * @returns Promise resolving to true if token is valid, false otherwise
   */
  verifyToken: async (token: string): Promise<boolean> => {
    try {
      console.info("JwtAuthService: Verifying token...");
      const isValid = await JwtApiService.verifyToken(token);
      console.info("JwtAuthService: Token verification result:", isValid);

      // Update store based on verification result
      if (isValid) {
        console.info(
          "JwtAuthService: Token is valid, setting isVerified to true",
        );
        jwtAuthStore.getState().setIsVerified(true);
        return true;
      } else {
        console.info("JwtAuthService: Token is invalid, clearing auth");
        jwtAuthStore.getState().setIsVerified(false);
        jwtAuthStore.getState().clearJwtAuth();
        return false;
      }
    } catch (error) {
      console.error("Error in JWT verification flow:", error);
      jwtAuthStore.getState().setIsVerified(false);
      jwtAuthStore.getState().clearJwtAuth();
      return false;
    }
  },

  /**
   * Logout the user
   */
  logout: async () => {
    await JwtApiService.logout();
    jwtAuthStore.getState().clearJwtAuth();
  },

  ttl: async () => {
    const ttl = await JwtApiService.ttl();
    return ttl;
  },

  /**
   * Initialize JWT authentication from URL if available
   */
  initializeFromUrl: async (): Promise<void> => {
    // Initialize the store with URL data
    console.info("JwtAuthService: Initializing from URL");
    jwtAuthStore.getState().initializeFromUrl();

    // Get the extracted token and authentication state
    const { jwtToken, isInKnownHost, isVerified } = jwtAuthStore.getState();
    console.info(
      "JwtAuthService: Token status - URL token or stored token exists:",
      !!jwtToken,
      "isInKnownHost:",
      isInKnownHost,
    );

    // Verify the token:
    if (jwtToken && isInKnownHost && !isVerified) {
      console.info("JwtAuthService: Will verify token");
      await JwtAuthService.verifyToken(jwtToken);
    }
  },

  /**
   * Get the current JWT token if available
   */
  getToken: (): string | null => {
    const { jwtToken, shouldUseJwt } = jwtAuthStore.getState();
    return shouldUseJwt ? jwtToken : null;
  },

  /**
   * Check if JWT auth should be used (on known host with valid token)
   */
  shouldUseJwtAuth: (): boolean => {
    return jwtAuthStore.getState().shouldUseJwt;
  },

  /**
   * Clear JWT authentication
   */
  clearAuth: (): void => {
    jwtAuthStore.getState().clearJwtAuth();
  },
};

export default JwtAuthService;
